// The one sanctioned use of React's `lazy`; everything else goes through lazyWithRecovery.
// eslint-disable-next-line no-restricted-imports
import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import {
  isStaleChunkError,
  reloadForNewBuild,
  reloadIfDeployedVersionChanged,
  STALE_CHUNK_TARGET,
} from "./appVersion";

/** Keeps the Suspense fallback on screen while the page reloads, instead of flashing an error. */
const waitForReload = <T>(): Promise<T> => new Promise<T>(() => undefined);

/**
 * Loads a chunk, recovering when a deploy has deleted it:
 * confirmed new deploy -> reload; otherwise retry once (network blip); if the chunk is still
 * gone -> reload with the stale-chunk target; if that reload is refused -> rethrow.
 */
export const loadWithRecovery = async <T>(importer: () => Promise<T>): Promise<T> => {
  try {
    return await importer();
  } catch (error) {
    if (!isStaleChunkError(error)) throw error;
  }

  if (await reloadIfDeployedVersionChanged()) return waitForReload<T>();

  try {
    return await importer();
  } catch (retryError) {
    if (!isStaleChunkError(retryError)) throw retryError;
    if (await reloadForNewBuild(STALE_CHUNK_TARGET)) return waitForReload<T>();
    throw retryError;
  }
};

/** Drop-in replacement for React.lazy that survives a deploy deleting the chunk. */
// Same constraint as React.lazy's own signature.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithRecovery = <T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): LazyExoticComponent<T> => lazy(() => loadWithRecovery(importer));
